import { useState } from "react";
import { useMutation } from "convex/react";
import { Building2, Loader2 } from "lucide-react";
import { Button, Card, CardContent, CardHeader, CardTitle } from "@repo/ui";
import { PAMI_REFERENTIEL_DIMENSIONS } from "@repo/utils";
import { api } from "../../../../convex/_generated/api";
import type { Id } from "../../../../convex/_generated/dataModel";

type Cible = "pami" | "pc-fit";

type Props = {
  campagneId: Id<"campagnes">;
  /** Cible à configurer — imposée par le test sélectionné dans la campagne. */
  cible: Cible;
};

const DESCRIPTION_PAMI =
  "UN référent — vous, ou quelqu'un d'autre — indique pour chacune des 20 dimensions le score attendu chez le candidat (bas, moyen ou élevé), ou qu'il n'y a pas d'attente particulière. Rien à composer, rien à faire passer de plus aux candidats.";

/**
 * Activation du référentiel d'une campagne (plan §6.1).
 *
 * NE CONCERNE PLUS QUE LE PAMI. Le fit-culturel n'a plus rien à activer : ses 23
 * items et sa pyramide sont imposés, donc son référentiel est posé avec la
 * campagne (et en rattrapage à l'ouverture pour les campagnes antérieures). Une
 * étape d'activation qui ne pose aucune question ne fait que retarder le seul
 * geste qui compte — désigner les référents, dans l'onglet « Référents ».
 *
 * Le PAMI garde la sienne : ses 20 dimensions sont imposées elles aussi, mais
 * son référentiel est à référent UNIQUE et l'activer reste une décision — la
 * campagne peut faire passer un PAMI sans pondérer quoi que ce soit.
 *
 * La cible n'est PAS arbitrée ici : une campagne qui fait passer les deux tests
 * porte les DEUX référentiels, et c'est le sélecteur de test de la campagne qui
 * dit lequel on regarde.
 */
export function ReferentielConfigSection({ campagneId, cible }: Props) {
  const configurer = useMutation(api.referentiel.campagne.configurerReferentiel);
  const [enCours, setEnCours] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);

  const activerPami = async () => {
    setErreur(null);
    setEnCours(true);
    try {
      await configurer({
        campagneId,
        config: {
          cible: "pami",
          dimensions: PAMI_REFERENTIEL_DIMENSIONS,
          echelle: { kind: "fourchette", min: 0, max: 10 },
        },
      });
    } catch (e) {
      setErreur(e instanceof Error ? e.message : String(e));
    } finally {
      setEnCours(false);
    }
  };

  // Fit-culturel sans référentiel : c'est le rattrapage qui est en train de le
  // poser (`assurerReferentielFitCulturel`), il n'y a rien à demander ici.
  if (cible === "pc-fit") {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Building2 className="h-5 w-5" />
            Référentiel entreprise
          </CardTitle>
        </CardHeader>
        <CardContent className="flex items-center gap-3">
          <Loader2 className="h-4 w-4 animate-spin text-primary" />
          <p className="text-sm text-muted-foreground">
            Préparation du référentiel PC Fit…
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Building2 className="h-5 w-5" />
            Référentiel entreprise
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-muted-foreground">
            Le référentiel décrit les attentes de votre organisation pour ce
            poste. Un ou plusieurs référents internes le
            remplissent&nbsp;; le score de matching de chaque candidat est
            ensuite calculé contre lui.
          </p>

          <p className="text-sm">
            <span className="font-medium">
              Pondère le test de personnalité et motivations (PAMI)
            </span>
            <span className="mt-1 block text-muted-foreground">
              {DESCRIPTION_PAMI}
            </span>
          </p>

          {erreur && <p className="text-sm text-destructive">{erreur}</p>}
          <Button onClick={() => void activerPami()} disabled={enCours}>
            {enCours && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Activer un référentiel PAMI
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
